import React, { FC } from 'react'

import { RewardsQuery } from '../../../../../../graphql/search'
import { usePractitionerSettings } from '../../../../../../lib/practitioner-settings/hooks/usePractitionerSettings'
import { rewardsLevelInfo, tierFromRewardsLevel } from '../../../../../../lib/rewards/levels'
import { monthName, parseRewardsPeriod } from '../../../../../../lib/rewards/periods'
import { dropZeroCents, formatAsCurrencyWithCommasOrZero } from '../../../../../../utils/priceTools'
import { RewardsHeading4 } from '../../RewardsHeading'
import { RewardsIconWrapper } from '../../RewardsIcon'
import * as Styled from './styled'

import Rebate from '../../../../../../img/rewards/arrow-undo.inline.svg'
import Orders from '../../../../../../img/rewards/bag.inline.svg'
import Commissions from '../../../../../../img/rewards/cash.inline.svg'
import Total from '../../../../../../img/rewards/checkmark-circle.inline.svg'
import Flash from '../../../../../../img/rewards/flash.inline.svg'

interface SalesLastMonthCardProps {
  className?: string
  previousSnapshot: NonNullable<
    NonNullable<RewardsQuery['customer']>['rewardsSnapshots']
  >['nodes'][number]
}

const MonthEarningDetail: FC<{ icon: React.ReactNode; text: string; amount: string }> = ({
  icon,
  text,
  amount,
}) => {
  return (
    <Styled.EarningsDetail>
      <RewardsIconWrapper>{icon}</RewardsIconWrapper>
      <Styled.EarningDetailText>{text}</Styled.EarningDetailText>
      <Styled.EarningDetailAmount>{amount}</Styled.EarningDetailAmount>
    </Styled.EarningsDetail>
  )
}

export const SalesLastMonthCard: FC<SalesLastMonthCardProps> = ({
  className,
  previousSnapshot,
}) => {
  const tier = tierFromRewardsLevel(previousSnapshot.level)
  const period = parseRewardsPeriod(previousSnapshot.period)
  const month = period?.month ? monthName(period.month) : null
  const sales = previousSnapshot.sales.total
  const orderCount = previousSnapshot.orderCount ?? 0
  const { rebatePercentage, commissionsPercentage } =
    rewardsLevelInfo(previousSnapshot.level)?.benefits ?? {}
  const commissionsEarned = parseFloat(previousSnapshot.commissionsEarned.total.amount)
  const rebatesEarned = parseFloat(previousSnapshot.rebatesEarned.total.amount)
  const totalEarned = parseFloat(previousSnapshot.totalEarned.total.amount)

  const [settings] = usePractitionerSettings()
  const isEligibleForRebates = !settings?.practitioner?.ineligibleForRebate

  return (
    <Styled.Card className={className}>
      <div>
        <RewardsHeading4>Qualifying Sales last month</RewardsHeading4>
        <Styled.Amount>
          {dropZeroCents(formatAsCurrencyWithCommasOrZero({ amount: parseFloat(sales.amount) }))}
        </Styled.Amount>
      </div>
      <Styled.Divider></Styled.Divider>
      <Styled.EarningsContainer>
        <Styled.EarningsHeader>Earnings{month ? ` for ${month}` : ''}</Styled.EarningsHeader>
        <Styled.EarningsDetailsContainer>
          <MonthEarningDetail icon={<Orders />} text="Orders" amount={`${orderCount}`} />
          <MonthEarningDetail
            icon={<Commissions />}
            text={`${commissionsPercentage == null ? '' : `${commissionsPercentage}% `}Commissions`}
            amount={dropZeroCents(formatAsCurrencyWithCommasOrZero({ amount: commissionsEarned }))}
          />
          {isEligibleForRebates && (
            <MonthEarningDetail
              icon={<Rebate />}
              text={`${rebatePercentage == null ? '' : `${rebatePercentage}% `}Rebate`}
              amount={dropZeroCents(formatAsCurrencyWithCommasOrZero({ amount: rebatesEarned }))}
            />
          )}
          <MonthEarningDetail
            icon={<Total />}
            text="Total earned"
            amount={dropZeroCents(formatAsCurrencyWithCommasOrZero({ amount: totalEarned }))}
          />
        </Styled.EarningsDetailsContainer>
      </Styled.EarningsContainer>
      <Styled.Divider></Styled.Divider>
      {tier != null && (
        <Styled.AchievementContainer>
          <RewardsIconWrapper>
            <Flash />
          </RewardsIconWrapper>
          <Styled.AchievementDetails>
            <Styled.AchievementHeader>Last month you achieved</Styled.AchievementHeader>
            <Styled.AchievementText>Tier {tier}</Styled.AchievementText>
          </Styled.AchievementDetails>
        </Styled.AchievementContainer>
      )}
    </Styled.Card>
  )
}
